import { CLASSIC_BOARD_GEOMETRY, getAccessibleCells, getGridNavigationTarget } from '../logic/classicBoardAccess';
import type { Key9Puzzle } from '../types/puzzle';

export function createAccessibleBoard(grid: HTMLElement, onSelect: (index: number) => void) {
  const geometry = CLASSIC_BOARD_GEOMETRY;
  grid.style.left = `${100 * geometry.x / geometry.width}%`;
  grid.style.top = `${100 * geometry.y / geometry.height}%`;
  grid.style.width = `${100 * 9 * geometry.cellSize / geometry.width}%`;
  grid.style.height = `${100 * 9 * geometry.cellSize / geometry.height}%`;
  let current = 0;
  const cells: HTMLElement[] = [];
  const select = (index: number) => {
    cells[current].tabIndex = -1;
    current = index;
    cells[index].tabIndex = 0;
    onSelect(index);
  };
  for (let row = 0; row < 9; row++) {
    const element = document.createElement('div');
    element.setAttribute('role', 'row');
    for (let column = 0; column < 9; column++) {
      const index = row * 9 + column;
      const cell = document.createElement('div');
      cell.id = `ds-cell-${index}`;
      cell.dataset.cell = String(index);
      cell.setAttribute('role', 'gridcell');
      cell.tabIndex = index === 0 ? 0 : -1;
      cell.addEventListener('focus', () => select(index));
      cell.addEventListener('click', () => {
        // VoiceOver activation and pointer taps use the same selection path.
        if (document.activeElement !== cell) cell.focus({ preventScroll: true });
        else select(index);
      });
      cells.push(cell);
      element.append(cell);
    }
    grid.append(element);
  }
  grid.addEventListener('keydown', (event) => {
    if (event.altKey || event.metaKey || event.shiftKey || event.isComposing) return;
    const target = getGridNavigationTarget(current, event.key, event.ctrlKey);
    if (target == null) return;
    event.preventDefault();
    cells[target].focus({ preventScroll: true });
  });

  return {
    render(puzzle: Key9Puzzle, board: string[], notes: string[], readOnly: boolean, selectedIndex: number | null) {
      grid.setAttribute('aria-readonly', String(readOnly));
      for (const model of getAccessibleCells(puzzle, board, notes, readOnly)) {
        const cell = cells[model.index];
        cell.setAttribute('aria-label', model.label);
        cell.setAttribute('aria-rowindex', String(model.row));
        cell.setAttribute('aria-colindex', String(model.column));
        cell.setAttribute('aria-readonly', String(model.readOnly));
        cell.setAttribute('aria-invalid', String(model.conflict));
        cell.setAttribute('aria-selected', String(model.index === selectedIndex));
      }
    }
  };
}
